import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["hiddenInputs", "selectionStrip", "contactRow", "sectionBody"];
  static values = {
    chatRoomId: String,
    removeLabel: { type: String, default: "Remove" },
    memberData: { type: Object, default: {} },
    adminIds: { type: Array, default: [] },
  };

  connect() {
    this.selectedMembers = new Map(); // id -> { name, avatarUrl, initial }

    // Who moderates the group is decided in step 2, not here. This step only
    // carries the roles through so they survive a search or a trip back.
    this.adminIds = new Set((this.adminIdsValue || []).map(String));

    // Primary source: server-provided member data (JSON value on the controller element)
    const memberData = this.memberDataValue || {};

    // Read initial selections from hidden inputs
    if (this.hasHiddenInputsTarget) {
      this.hiddenInputsTarget.querySelectorAll("input[name='member_ids[]']").forEach((input) => {
        const id = input.value;
        const serverName = memberData[id] || input.dataset.userName || "";
        const initial = (serverName.charAt(0) || "?").toUpperCase();
        this.selectedMembers.set(id, { name: serverName, avatarUrl: null, initial });
      });
    }

    // Enrich with avatar data from the server-rendered chips in the strip if any.
    // Scoped to the chip itself — its remove button carries the same user id but
    // none of the data, and would blank the avatar again.
    if (this.hasSelectionStripTarget) {
      this.selectionStripTarget.querySelectorAll(".member-chip[data-user-id]").forEach((chip) => {
        const id = String(chip.dataset.userId);
        const existing = this.selectedMembers.get(id);
        const name = chip.dataset.userName || existing?.name || "";
        const avatarUrl = this.avatarUrlFrom(chip) || existing?.avatarUrl || null;
        const initial = (name.charAt(0) || "?").toUpperCase();
        this.selectedMembers.set(id, { name, avatarUrl, initial });
      });
    }

    this.nextButton = document.getElementById("member-picker-next-button");
    this.counterEl = document.getElementById("member-picker-counter");
    this.selectionContainer = this.element.querySelector(".member-picker__selection-container");

    if (this.nextButton) {
      this.handleNextClick = this.handleNextClick.bind(this);
      this.nextButton.addEventListener("click", this.handleNextClick);
    }

    this.updateUI();
  }

  disconnect() {
    if (this.nextButton) {
      this.nextButton.removeEventListener("click", this.handleNextClick);
    }
  }

  toggleMember(event) {
    const row = event.currentTarget;
    const userId = String(row.dataset.userId || "");
    if (!userId) return;

    const name = row.dataset.userName || "";
    const avatarUrl = this.avatarUrlFrom(row);
    const initial = (name.charAt(0) || "?").toUpperCase();

    if (this.selectedMembers.has(userId)) {
      this.selectedMembers.delete(userId);
      // Taking someone out of the group takes their moderator role with them —
      // adding them back here adds a plain member.
      this.adminIds.delete(userId);
    } else {
      this.selectedMembers.set(userId, { name, avatarUrl, initial });
    }

    this.updateUI();
  }


  removeChip(event) {
    event.stopPropagation();
    event.preventDefault();
    const btn = event.currentTarget;
    const userId = String(btn.dataset.userId || btn.dataset.memberPickerUserIdParam || "");
    if (!userId) return;

    this.selectedMembers.delete(userId);
    this.adminIds.delete(userId);
    this.updateUI();
  }

  // The button carries its own state in its label — "Alle auswählen" until
  // nothing is left to add, "Alle abwählen" after that — so it does whatever it
  // currently says rather than tracking a separate checked flag.
  toggleSelectAll(event) {
    event.preventDefault();
    event.stopPropagation();
    const section = event.currentTarget.closest(".member-picker__section");
    if (!section) return;

    const rows = this.sectionRows(section);
    const select = !this.allRowsSelected(rows);

    rows.forEach((row) => {
      const userId = String(row.dataset.userId || "");
      if (!userId) return;
      const name = row.dataset.userName || "";
      const avatarUrl = this.avatarUrlFrom(row);
      const initial = (name.charAt(0) || "?").toUpperCase();

      if (select) {
        this.selectedMembers.set(userId, { name, avatarUrl, initial });
      } else {
        this.selectedMembers.delete(userId);
        this.adminIds.delete(userId);
      }
    });

    this.updateUI();
  }

  // The avatar is already rendered in the row (and in a server-rendered chip),
  // so the chip reuses that exact src — no second URL for the same variant, and
  // the browser serves it straight from cache.
  avatarUrlFrom(element) {
    return element?.querySelector(".bryght-room-avatar img")?.getAttribute("src") || null;
  }

  sectionRows(section) {
    return Array.from(section.querySelectorAll(".member-picker__contact-row"));
  }

  allRowsSelected(rows) {
    return rows.length > 0 && rows.every((row) => this.selectedMembers.has(String(row.dataset.userId || "")));
  }

  handleNextClick(event) {
    event.preventDefault();
    if (this.selectedMembers.size === 0) return;

    const ids = Array.from(this.selectedMembers.keys());
    const params = new URLSearchParams();
    ids.forEach((id) => {
      params.append("member_ids[]", id);
      if (this.adminIds.has(id)) params.append("admin_ids[]", id);
    });

    let url;
    if (this.chatRoomIdValue) {
      params.append("chat_room_id", this.chatRoomIdValue);
      url = `/chat_group_rooms/${this.chatRoomIdValue}/edit?${params.toString()}`;
    } else {
      url = `/chat_group_rooms/new?${params.toString()}`;
    }

    const frame = document.getElementById("chat_modal") || document.getElementById("dialog_modal");
    if (frame) {
      frame.src = url;
    }
  }

  updateUI() {
    const count = this.selectedMembers.size;

    // 1. Update counter
    if (this.counterEl) {
      this.counterEl.textContent = count;
    }

    // 2. Update next button state
    if (this.nextButton) {
      this.nextButton.disabled = count === 0;
    }

    // 3. Update selection container visibility
    if (this.selectionContainer) {
      if (count > 0) {
        this.selectionContainer.classList.add("member-picker__selection-container--visible");
      } else {
        this.selectionContainer.classList.remove("member-picker__selection-container--visible");
      }
    }

    // 4. Update hidden inputs for search survival
    if (this.hasHiddenInputsTarget) {
      this.hiddenInputsTarget.innerHTML = "";
      this.selectedMembers.forEach((info, id) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "member_ids[]";
        input.value = id;
        input.dataset.userName = info.name || "";
        this.hiddenInputsTarget.appendChild(input);

        if (this.adminIds.has(id)) {
          const adminInput = document.createElement("input");
          adminInput.type = "hidden";
          adminInput.name = "admin_ids[]";
          adminInput.value = id;
          this.hiddenInputsTarget.appendChild(adminInput);
        }
      });
    }

    // 5. Update selection strip chips
    if (this.hasSelectionStripTarget) {
      this.selectionStripTarget.innerHTML = "";
      this.selectedMembers.forEach((info, id) => {
        const chip = this.createChipElement(id, info);
        this.selectionStripTarget.appendChild(chip);
      });
    }

    // 6. Update checkmarks on visible rows
    this.element.querySelectorAll(".member-picker__contact-row").forEach((row) => {
      const userId = String(row.dataset.userId || "");
      const isSelected = this.selectedMembers.has(userId);
      const checkEl = row.querySelector(".member-picker__contact-check");
      if (checkEl) {
        if (isSelected) {
          checkEl.classList.add("member-picker__contact-check--active");
          row.classList.add("member-picker__contact-row--selected");
        } else {
          checkEl.classList.remove("member-picker__contact-check--active");
          row.classList.remove("member-picker__contact-row--selected");
        }
      }
    });

    // 7. Flip the select-all label between "Alle auswählen" and "Alle abwählen"
    this.element.querySelectorAll(".member-picker__section").forEach((section) => {
      const toggle = section.querySelector(".member-picker__select-all");
      if (!toggle) return;

      const rows = this.sectionRows(section);
      if (rows.length === 0) return;

      toggle.textContent = this.allRowsSelected(rows) ? toggle.dataset.deselectLabel : toggle.dataset.selectLabel;
    });
  }

  createChipElement(id, info) {
    const chip = document.createElement("div");
    chip.className = "member-chip";
    chip.dataset.userId = id;
    chip.dataset.userName = info.name;
    chip.dataset.avatarUrl = info.avatarUrl || "";
    chip.dataset.initial = info.initial || "?";

    const avatarWrap = document.createElement("div");
    avatarWrap.className = "member-chip__avatar";

    const avatar = document.createElement("div");
    avatar.className = "bryght-room-avatar bryght-room-avatar--small";
    if (info.avatarUrl) {
      avatar.innerHTML = `<img src="${info.avatarUrl}" alt="" />`;
    } else {
      avatar.classList.add("bryght-room-avatar--initials");
      avatar.textContent = info.initial || "?";
    }
    avatarWrap.appendChild(avatar);

    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className = "member-chip__remove";
    removeBtn.setAttribute("aria-label", this.removeLabelValue);
    removeBtn.dataset.action = "click->member-picker#removeChip";
    removeBtn.dataset.userId = id;
    removeBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;
    avatarWrap.appendChild(removeBtn);

    chip.appendChild(avatarWrap);

    const nameEl = document.createElement("span");
    nameEl.className = "member-chip__name";
    nameEl.textContent = info.name || "?";
    chip.appendChild(nameEl);

    return chip;
  }
}
